/** Small presentation helpers shared across pages. */

/** Formats an ISO timestamp for table display, e.g. "20 May 2026, 14:30". */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) {
    return "—";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

/** Formats an ISO timestamp as a date only, e.g. "20 May 2026". */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) {
    return "—";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
}

/** A compact schedule range, e.g. "20 May 14:30 → 20 May 18:00". */
export function formatRange(from: string, to: string): string {
  const opts: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit"
  };
  const a = new Date(from);
  const b = new Date(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) {
    return `${from} → ${to}`;
  }
  return `${a.toLocaleString("en-GB", opts)} → ${b.toLocaleString("en-GB", opts)}`;
}

/** Converts an ISO timestamp to the value a datetime-local input expects. */
export function toDateTimeLocal(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

/** Converts a datetime-local input value back to an ISO 8601 string. */
export function fromDateTimeLocal(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toISOString();
}

/** Suggests a URL slug from a free-text name. */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Human-readable label for an audit action code, e.g. "event.create" → "Event created". */
export function auditActionLabel(action: string): string {
  const map: Record<string, string> = {
    "customer.create": "Customer created",
    "customer.update": "Customer updated",
    "event.create": "Event created",
    "event.update": "Event updated",
    "event.rotate_webhook_secret": "Webhook secret rotated",
    "operator.create": "Operator created",
    "operator.update": "Operator updated",
    "operator.reset_password": "Operator password reset",
    "admin_user.create": "Admin user created",
    "admin_user.update": "Admin user updated",
    "admin_user.reset_password": "Admin password reset",
    "attendee.create": "Attendee created",
    "attendee.update": "Attendee updated",
    "attendee.delete": "Attendee deleted",
    "event_operator.assign": "Operator assigned",
    "event_operator.unassign": "Operator unassigned",
    "meetpoint.create": "Meetpoint configured",
    "meetpoint.update": "Meetpoint updated",
    "meetpoint.delete": "Meetpoint removed",
    "meetpoint.sync": "Meetpoint sync triggered"
  };
  return map[action] ?? action;
}
